import { Link, useParams, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Button, Progress } from 'antd';
import { toast } from 'react-toastify';
import {
  fetchUsersPersonalSurvey,
  updatePersonalSurveyPartA,
  submitPersonalSurveyHealthConsent,
  updatePersonalSurveyModule1,
  updatePersonalSurveyModule2,
  updatePersonalSurveyModule3,
  updatePersonalSurveyModule4,
  updatePersonalSurveyModule5,
  updatePersonalSurveyModule6,
  updatePersonalSurveyModule7,
  updatePersonalSurveyModule8,
  exportPersonalSurvey,
} from '../../../api/user-api';
import { Container } from '../../../components/content';
import ContentLayout from '../../../layout/ContentLayout';
import SurveySection from '../../../components/personal-survey/SurveySection';
import LegacySurveyPanel from '../../../components/personal-survey/LegacySurveyPanel';
import AiSummaryCard from '../../../components/user/AiSummaryCard';
import ClientProfileCard from '../../../components/user/ClientProfileCard';
import ClientActivityPanel from '../../../components/user/ClientActivityPanel';
import { usePermissions } from '../../../hooks/usePermissions';
import { SURVEY_SECTIONS } from '../../../constants/personal-survey-fields';
import styles from '../../../styles/personal-survey.module.scss';

const UPDATE_FN = {
  partA: updatePersonalSurveyPartA,
  module1: updatePersonalSurveyModule1,
  module2: updatePersonalSurveyModule2,
  module3: updatePersonalSurveyModule3,
  module4: updatePersonalSurveyModule4,
  module5: updatePersonalSurveyModule5,
  module6: updatePersonalSurveyModule6,
  module7: updatePersonalSurveyModule7,
  module8: updatePersonalSurveyModule8,
};

const PersonalSurvey = () => {
  const { id } = useParams();
  const location = useLocation();
  const { can: canDo, loading: permissionsLoading } = usePermissions();
  // Each block follows one permission; the API enforces the same ones (and, for anyone but
  // an admin, only for the viewer's own clients), so hiding here is convenience only.
  const canReadSummary = canDo('ai:summary:read');
  const canReadProfile = canDo('users:read');
  const canReadContact = canDo('data:contact:read');
  const canReadSurvey = canDo('data:survey:read');
  const canReadHealth = canDo('data:health:read');
  const canReadActivity = canDo('data:activity:read');
  const canReadOrders = canDo('data:orders:read');
  // Survey writes on someone else's survey need platform-wide scope in the API
  // (checkOwnershipOrAdmin).
  const canEditSurvey = canDo('scope:all');
  const backTo = location.state?.from || '/korisnici';
  const displayName = [location.state?.firstName, location.state?.lastName]
    .filter(Boolean)
    .join(' ');

  const [survey, setSurvey] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);

  const getSurveyData = useCallback(async () => {
    const { data, success, message } = await fetchUsersPersonalSurvey(id);

    if (!success) {
      toast.error(message || 'Greška pri učitavanju ankete.');
      return;
    }

    setSurvey(data);
  }, [id]);

  useEffect(() => {
    if (!permissionsLoading && canReadSurvey) getSurveyData();
  }, [getSurveyData, permissionsLoading, canReadSurvey]);

  const handleSaveSection = async (sectionKey, draft) => {
    const updateFn = UPDATE_FN[sectionKey];
    const { success, message } = await updateFn({ id, payload: draft });

    if (!success) {
      toast.error(message);
      return false;
    }

    toast.success(message);
    await getSurveyData();
    return true;
  };

  const handleToggleExpand = (key, isOpen) => {
    if (editingKey && editingKey !== key) {
      toast.info('Sačuvajte ili otkažite izmene pre otvaranja drugog dela.');
      return;
    }
    setExpandedKey(isOpen ? key : null);
  };

  const handleEditStart = (key) => {
    setEditingKey(key);
    setExpandedKey(key);
  };

  const handleEditEnd = (key) => {
    setEditingKey((prev) => (prev === key ? null : prev));
  };

  const handleGiveConsent = async () => {
    const { success, message } = await submitPersonalSurveyHealthConsent(id);

    if (!success) {
      toast.error(message);
      return;
    }

    toast.success(message);
    await getSurveyData();
  };

  const handleExport = async () => {
    setExporting(true);
    const result = await exportPersonalSurvey(id);
    setExporting(false);

    if (!result.success) {
      toast.error(result.message);
    } else if (result.data === null) {
      toast.info(result.message);
    } else {
      toast.success('Anketa uspešno izvezena');
    }
  };

  const progress = survey?.progress;

  return (
    <Container>
      <Link to={backTo} className={`link_btn ${styles.backLink}`}>
        <img alt="back image" src="/assets/icons/chevron.svg" />
        <span>Nazad</span>
      </Link>
      <ContentLayout pt={true}>
        <div className={styles.header}>
          <p className="title">
            Detalji korisnika{displayName ? `: ${displayName}` : ''}
          </p>
          <div className={styles.headerActions}>
            {canReadOrders && (
              <Link
                to={`/korisnik/narudzbine/${id}`}
                state={{ ...location.state, from: location.pathname }}
                className="btn-tertiary btn-small"
              >
                Porudžbine
              </Link>
            )}
            {canReadSurvey && (
              <Button
                className="btn-primary btn-small"
                onClick={handleExport}
                loading={exporting}
              >
                Izvezi CSV
              </Button>
            )}
          </div>
        </div>

        {canReadProfile && id && (
          <ClientProfileCard key={`p-${id}`} userId={id} canSeeContact={canReadContact} />
        )}

        {/* Gated by permission, not by role: an admin still sees it (admins hold every
            permission), and a role granted ai:summary:read now sees it too. The engine
            enforces the same permission server side. */}
        {canReadSummary && id && <AiSummaryCard key={id} userId={id} />}

        {canReadActivity && id && <ClientActivityPanel key={`a-${id}`} userId={id} />}

        {!permissionsLoading && !canReadSurvey && (
          <p className={styles.locked}>Vaša rola nema pristup odgovorima iz upitnika.</p>
        )}

        {progress && (
          <div className={styles.progress}>
            <Progress type="circle" percent={progress.percentComplete} size={72} />
            <div className={styles.progress__stats}>
              <span>Osnovna anketa: {progress.partACompleted ? 'Popunjeno' : 'Nije popunjeno'}</span>
              <span>
                Popunjeno modula: {progress.completedOptionalModules}/{progress.totalOptionalModules}
              </span>
            </div>
          </div>
        )}

        {survey && SURVEY_SECTIONS.map((section) => {
          const isModule6 = section.key === 'module6';
          if (isModule6 && !canReadHealth) {
            return (
              <p key={section.key} className={styles.locked}>
                {section.title}: zdravstveni podaci su sakriveni - vaša rola nema pristup
                zdravstvenim podacima.
              </p>
            );
          }
          const partACompleted = !!survey?.partA;
          const consentGiven = !!survey?.healthDataConsent?.givenAt;

          let disabled = false;
          let disabledReason;
          let extraAction;

          if (section.key !== 'partA' && !partACompleted) {
            disabled = true;
            disabledReason = 'Osnovna anketa mora biti popunjena pre uređivanja ovog modula.';
          } else if (isModule6 && !consentGiven) {
            disabled = true;
            disabledReason = 'Potrebna je saglasnost korisnika pre unosa zdravstvenih podataka.';
            extraAction = canEditSurvey && (
              <Button className="btn-tertiary btn-small" onClick={handleGiveConsent}>
                Zabeleži saglasnost
              </Button>
            );
          }

          return (
            <SurveySection
              key={section.key}
              title={section.title}
              fieldConfig={section.fieldConfig}
              sectionData={survey?.[section.key]}
              completedAt={survey?.[section.key]?.completedAt}
              onSave={(draft) => handleSaveSection(section.key, draft)}
              disabled={disabled}
              disabledReason={disabledReason}
              extraAction={extraAction}
              isExpanded={expandedKey === section.key}
              onToggleExpand={(isOpen) => handleToggleExpand(section.key, isOpen)}
              canEdit={!editingKey || editingKey === section.key}
              readOnly={!canEditSurvey}
              onEditStart={() => handleEditStart(section.key)}
              onEditEnd={() => handleEditEnd(section.key)}
            />
          );
        })}

        {survey && <LegacySurveyPanel legacy={survey?.legacy} />}
      </ContentLayout>
    </Container>
  );
};

export default PersonalSurvey;
